// Authentication utility functions
import axios from 'axios';
import { BASE_URL } from '../config';

const TOKEN_KEY      = 'accessToken';
const REFRESH_KEY    = 'refreshToken';
const ROLE_KEY       = 'userRole';
const HOSPITAL_ID_KEY = 'hospitalId';
const CLINIC_ID_KEY  = 'clinicId';
const USER_ID_KEY    = 'userId';

// ── token helpers ──────────────────────────────────────────────────────────
export const saveAuth = (token, role, hospitalId, clinicId, userId) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(ROLE_KEY, role);
  localStorage.setItem(HOSPITAL_ID_KEY, hospitalId);
  localStorage.setItem(CLINIC_ID_KEY, clinicId);
  if (userId) localStorage.setItem(USER_ID_KEY, userId);
};

export const getToken      = () => localStorage.getItem(TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);
export const getRole       = () => localStorage.getItem(ROLE_KEY);
export const getHospitalId = () => localStorage.getItem(HOSPITAL_ID_KEY);
export const getClinicId   = () => localStorage.getItem(CLINIC_ID_KEY);
export const getUserId     = () => localStorage.getItem(USER_ID_KEY);

export const isAuthenticated = () => {
  const token = getToken();
  const role  = getRole();
  if (!token || !role) {
    if (token || role) clearAuth();
    return false;
  }
  return true;
};

export const clearAuth = () => {
  [TOKEN_KEY, REFRESH_KEY, ROLE_KEY, HOSPITAL_ID_KEY,
   CLINIC_ID_KEY, USER_ID_KEY,
   'selectedHospitalId', 'selectedHospitalName'].forEach(k => localStorage.removeItem(k));
};

export const logout = () => {
  clearAuth();
  window.location.href = '/?logout=true';
};

// ── axios interceptor — auto-refresh on 401 ────────────────────────────────
let isRefreshing = false;
let failedQueue  = [];   // requests waiting while token is being refreshed

const processQueue = (error, token = null) => {
  failedQueue.forEach(p => error ? p.reject(error) : p.resolve(token));
  failedQueue = [];
};

axios.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config;

    // Only handle 401 once per request (avoid infinite loop)
    if (err.response?.status !== 401 || original._retry) {
      return Promise.reject(err);
    }

    // Don't try to refresh on the login or refresh endpoints themselves
    if (
      original.url?.includes('/login/') ||
      original.url?.includes('/token/refresh/')
    ) {
      return Promise.reject(err);
    }

    original._retry = true;

    if (isRefreshing) {
      // Queue this request until the refresh completes
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(newToken => {
        original.headers['Authorization'] = `Bearer ${newToken}`;
        return axios(original);
      });
    }

    isRefreshing = true;
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      // No refresh token — send to login
      logout();
      return Promise.reject(err);
    }

    try {
      const apiBase = BASE_URL.replace(/\/$/, '');
      const res = await axios.post(
        `${apiBase}/api/token/refresh/`,
        { refresh: refreshToken },
        { _retry: true }   // prevent this call from triggering the interceptor again
      );

      const newAccess  = res.data.access;
      const newRefresh = res.data.refresh; // present when ROTATE_REFRESH_TOKENS=True

      localStorage.setItem(TOKEN_KEY, newAccess);
      if (newRefresh) localStorage.setItem(REFRESH_KEY, newRefresh);

      // Update default header for all future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;

      // Retry all queued requests with the new token
      processQueue(null, newAccess);

      // Retry the original failed request
      original.headers['Authorization'] = `Bearer ${newAccess}`;
      return axios(original);
    } catch (refreshErr) {
      processQueue(refreshErr, null);
      // Refresh failed — session is truly expired, force logout
      clearAuth();
      window.location.href = '/?logout=true&reason=session_expired';
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);
